import { Formik, Form, type FormikHelpers } from "formik";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { toast } from "@volca/ui/lib/toast";
import { useImpersonation } from "@/core/impersonation";
import { ImpersonationFields } from "./impersonation-fields";
import type { ImpersonationFormValues } from "./types";

interface ImpersonationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialValues: ImpersonationFormValues = {
  parentType: "business",
  selectedParentId: "",
  selectedUserId: "",
  parentSearchValue: "",
  userSearchValue: "",
};

const impersonationSchema = Yup.object().shape({
  parentType: Yup.string()
    .oneOf(["tenant", "business"])
    .required("Parent type is required"),
  selectedParentId: Yup.string().required("Please select a parent"),
  selectedUserId: Yup.string().required("Please select a user to impersonate"),
  parentSearchValue: Yup.string(),
  userSearchValue: Yup.string(),
});

export const ImpersonationModal = ({
  open,
  onOpenChange,
}: ImpersonationModalProps) => {
  const { startImpersonation, isSavePending } = useImpersonation();
  const handleSubmit = async (
    values: ImpersonationFormValues,
    { setSubmitting, resetForm }: FormikHelpers<ImpersonationFormValues>,
  ) => {
    try {
      await startImpersonation(values.selectedUserId);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to start impersonation", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={impersonationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ isSubmitting, isValid }) => (
        <Dialog
          open={open}
          onOpenChange={(nextOpen) => {
            if (!isSubmitting) onOpenChange(nextOpen);
          }}
        >
          <DialogContent
            className="sm:max-w-[500px]"
            showCloseButton={!isSubmitting}
          >
            <DialogDescription className="sr-only">
              Select a tenant or business and user to impersonate
            </DialogDescription>
            <Form className="grid gap-4">
              <DialogHeader>
                <DialogTitle>Impersonate user</DialogTitle>
              </DialogHeader>
              <ImpersonationFields open={open} />
              {isSavePending && (
                <p role="status" className="text-xs">
                  A save is in progress. Wait for it to finish before switching
                  users.
                </p>
              )}
              <DialogFooter>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || isSavePending || !isValid}
                >
                  Start Impersonation
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  );
};
